import React from "react";
import "./Modal.scss";

interface ModalSectionProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export const ModalSection: React.FC<ModalSectionProps> = ({
  title,
  icon,
  children,
  className = "",
}) => {
  return (
    <div className={`modal-section ${className}`}>
      <div className="section-header">
        {icon && <span className="section-icon">{icon}</span>}
        <h3 className="section-title">{title}</h3>
      </div>
      <div className="section-content">{children}</div>
    </div>
  );
};

interface ModalInfoItemProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

export const ModalInfoItem: React.FC<ModalInfoItemProps> = ({
  label,
  value,
  className = "",
}) => {
  return (
    <div className={`info-item ${className}`}>
      <p className="info-label">{label}</p>
      <div className="info-value">{value}</div>
    </div>
  );
};

interface ModalInfoGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}

export const ModalInfoGrid: React.FC<ModalInfoGridProps> = ({
  children,
  columns = 2,
  className = "",
}) => {
  const gridClass =
    columns === 1
      ? "grid-cols-1"
      : columns === 3
      ? "grid-cols-3"
      : "grid-cols-2";

  return (
    <div className={`info-grid ${gridClass} ${className}`}>{children}</div>
  );
};

interface ModalFormGroupProps {
  label: string;
  children: React.ReactNode;
  helpText?: string;
  error?: string;
  required?: boolean;
  helpTextType?: "default" | "error" | "success" | "warning";
}

export const ModalFormGroup: React.FC<ModalFormGroupProps> = ({
  label,
  children,
  helpText,
  error,
  required = false,
  helpTextType = "default",
}) => {
  // Determine the help text to show and its type
  const displayHelpText = error || helpText;
  const helpTextClass = error ? "error" : helpTextType;

  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {required && <span className="text-[#ff0000] ml-1">*</span>}
      </label>
      {children}
      {displayHelpText && (
        <p className={`form-help ${helpTextClass}`}>{displayHelpText}</p>
      )}
    </div>
  );
};

interface ModalButtonProps {
  variant?: "primary" | "secondary" | "danger";
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: "button" | "submit";
}

export const ModalButton: React.FC<ModalButtonProps> = ({
  variant = "primary",
  children,
  onClick,
  disabled = false,
  className = "",
  type = "button",
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`modal-button btn-${variant} ${className}`}
    >
      {children}
    </button>
  );
};

interface ModalErrorProps {
  message: string;
  className?: string;
}

export const ModalError: React.FC<ModalErrorProps> = ({
  message,
  className = "",
}) => {
  return (
    <div className={`modal-error ${className}`}>
      <div className="error-content">
        <svg className="error-icon" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
        <p className="error-message">{message}</p>
      </div>
    </div>
  );
};

interface ModalWarningProps {
  title: string;
  description: string;
  className?: string;
}

export const ModalWarning: React.FC<ModalWarningProps> = ({
  title,
  description,
  className = "",
}) => {
  return (
    <div className={`modal-warning ${className}`}>
      <div className="warning-content">
        <svg className="warning-icon" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        <div className="warning-text">
          <h3 className="warning-title">{title}</h3>
          <p className="warning-description">{description}</p>
        </div>
      </div>
    </div>
  );
};

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  className = "",
}) => {
  return <div className={`modal-footer-content ${className}`}>{children}</div>;
};

// Common Icons
export const ModalIcons = {
  Info: (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  ),
  Container: (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
      />
    </svg>
  ),
  User: (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  ),
  Location: (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
  Edit: (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
      />
    </svg>
  ),
  Warning: (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
      />
    </svg>
  ),
};
